import React, { useEffect } from "react";
import { ArrowDown, ArrowLeft, ArrowRight, ArrowUp, Home, Navigation } from "lucide-react";
import { L, RTL_LANGS, tr } from "@/lib/i18n";

const ICONS = { left: ArrowLeft, right: ArrowRight, up: ArrowUp, down: ArrowDown };
const KEYS = { left: "ArrowLeft", right: "ArrowRight", up: "ArrowUp", down: "ArrowDown" };
const INTERACTIVE = 'input, textarea, select, button, a[href], summary, audio[controls], video[controls], [contenteditable]:not([contenteditable="false"]), [role="textbox"], [role="combobox"], [role="spinbutton"], [role="slider"], [role="listbox"], [role="tree"], [role="grid"], [role="tablist"], [role="menu"], [role="menubar"], [role="button"], [role="link"], [role="switch"]';
const OVERLAYS = 'dialog[open], [role="dialog"], [role="alertdialog"], [aria-modal="true"], [role="menu"], [role="listbox"], [data-radix-popper-content-wrapper], [popover]';

// Preserve the existing visible arrow mapping. Keyboard shortcuts are derived
// from these same icons, never independently from the drag direction.
export function visibleNavigationDirection(swipeDirection, lang) {
  const rtl = RTL_LANGS.has((lang || "en").split("-")[0]);
  if (!rtl && swipeDirection === "left") return "right";
  if (!rtl && swipeDirection === "right") return "left";
  return swipeDirection;
}

function isInteractive(target) {
  if (target?.nodeType !== 1) return false;
  if (target.isContentEditable || target.closest('input, textarea, select, [contenteditable]:not([contenteditable="false"])')) return true;
  const control = target.closest(INTERACTIVE);
  if (control?.tagName === "BUTTON" && control.closest('[data-testid="viewer-bottom-nav"]')) return false;
  return !!control;
}

function hasOpenOverlay() {
  return [...document.querySelectorAll(OVERLAYS)].some(element => {
    if (element.hasAttribute("popover")) {
      try { if (!element.matches(":popover-open")) return false; }
      catch (_) { if (element.dataset.state !== "open") return false; }
    }
    for (let node = element; node; node = node.parentElement) {
      if (node.hidden || node.getAttribute("aria-hidden") === "true" || node.dataset.state === "closed") return false;
      const style = window.getComputedStyle(node);
      if (style.display === "none" || style.visibility === "hidden" || style.visibility === "collapse") return false;
    }
    return true;
  });
}

export default function SceneNavigation({
  scene, transitions = [], scenes = [], project = {}, lang,
  onNavigate, onOverview, busy = false, keyboardEnabled = false,
}) {
  const currentLang = lang || project.default_language || "en";
  useEffect(() => {
    if (!keyboardEnabled || busy) return undefined;
    const onKeyDown = event => {
      if (event.defaultPrevented || event.repeat || event.isComposing || event.keyCode === 229
        || event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
      if (!Object.values(KEYS).includes(event.key)) return;
      if (isInteractive(document.activeElement)
        || (event.composedPath?.() || [event.target]).some(isInteractive)
        || hasOpenOverlay()) return;
      const transition = transitions.find(item => KEYS[visibleNavigationDirection(item.swipe_direction, currentLang)] === event.key);
      if (transition && onNavigate?.(transition, "keyboard") === true) event.preventDefault();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [keyboardEnabled, busy, transitions, currentLang, onNavigate]);

  return (
    <div className="absolute bottom-0 inset-x-0 z-30 flex flex-col items-center pb-6 md:pb-8 gap-3 pointer-events-none">
      <div className="text-[10px] uppercase tracking-[0.3em] text-white/50 pointer-events-auto" data-testid="viewer-scene-name">
        {tr(scene, "name", currentLang, project.default_language) || scene?.name}
      </div>
      <div className="pill-nav flex flex-wrap justify-center items-center gap-1 max-w-[95vw] px-2 py-1.5 rounded-full pointer-events-auto"
        data-testid="viewer-bottom-nav" aria-busy={busy}>
        <button type="button" onClick={onOverview} disabled={busy} data-testid="viewer-overview-btn"
          className="min-h-[44px] min-w-[44px] px-3 py-2 text-white/85 hover:text-white text-xs tracking-wider uppercase inline-flex items-center justify-center gap-2 rounded-full hover:bg-white/10 disabled:opacity-40 disabled:cursor-wait transition">
          <Home className="h-4 w-4" aria-hidden="true" /> {L(currentLang, "overview")}
        </button>
        {transitions.map(transition => {
          const direction = visibleNavigationDirection(transition.swipe_direction, currentLang);
          const Icon = ICONS[direction] || Navigation;
          const target = scenes.find(item => item.id === transition.to_scene_id);
          const targetLabel = tr(transition, "button_label", currentLang, project.default_language)
            || tr(target, "name", currentLang, project.default_language);
          const label = targetLabel ? `${L(currentLang, "go_to_view")}: ${targetLabel}` : L(currentLang, "go_to_view");
          return (
            <button key={transition.id} type="button" disabled={busy}
              onClick={() => onNavigate?.(transition, "bottom_nav_button")}
              data-testid={`viewer-trans-${transition.id}`} aria-label={label} aria-keyshortcuts={KEYS[direction]}
              className="min-h-[44px] min-w-[44px] p-3 text-white/85 hover:text-white inline-flex items-center justify-center rounded-full hover:bg-white/10 disabled:opacity-40 disabled:cursor-wait transition">
              <Icon className="h-4 w-4" aria-hidden="true" />
            </button>
          );
        })}
      </div>
    </div>
  );
}
